import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import { Check, Lock } from 'lucide-react';
import { Drawer, DrawerContent, DrawerTitle } from '../ui/drawer';
import StarRating, { scoreLabel } from './StarRating';
import RatingForm, { reasonLabel, detailLabel } from './RatingForm';
import { initials } from '../../utils/initials';
import {
  useInterventionRating,
  useRateIntervention,
  useDeleteInterventionRating,
} from '../../hooks/useInterventionRating';
import type { RateInterventionBody } from '../../api/interventionRatingApi';

interface RatingCardProps {
  issueKey: string;
  // Admin view: show the rating, never the actions.
  readOnly?: boolean;
}

// Renders nothing until the ticket has a rating row the caller may see.
// Layout mirrors the mockup: avatar · title/subtitle header, wide star row, lock footnote.
const RatingCard = ({ issueKey, readOnly = false }: RatingCardProps) => {
  const { t } = useTranslation();
  const { data: rating, isLoading } = useInterventionRating(issueKey);
  const { mutate: rate, isPending: isRating } = useRateIntervention(issueKey);
  const { mutate: remove, isPending: isDeleting } = useDeleteInterventionRating(issueKey);
  const [open, setOpen] = useState(false);
  const [preselected, setPreselected] = useState(0);

  if (isLoading || !rating) return null;

  const canEdit = rating.canRate && !readOnly;
  const openWith = (score: number) => {
    setPreselected(score);
    setOpen(true);
  };
  const submit = (body: RateInterventionBody) => rate(body, { onSuccess: () => setOpen(false) });

  const sentSubtitle =
    rating.score != null
      ? [scoreLabel(t, rating.score), rating.ratedAt ? t('sent on {{date}}', { date: dayjs(rating.ratedAt).format('LL') }) : '']
          .filter(Boolean)
          .join(' · ')
      : '';

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 sm:p-6">
      {rating.score != null ? (
        <>
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-green-100 text-green-600" aria-hidden="true">
              <Check className="h-5 w-5" strokeWidth={2.5} />
            </div>
            <div className="min-w-0 flex-1">
              <h3 className="truncate text-base font-semibold text-gray-900">{t('Service rated')}</h3>
              <p className="truncate text-xs text-gray-500">{sentSubtitle}</p>
            </div>
            <StarRating value={rating.score} readOnly className="shrink-0" />
          </div>
          {readOnly && (
            <div className="mt-3 space-y-2">
              {rating.reasons.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {rating.reasons.map((reason) => (
                    <span key={reason} className="rounded-full bg-gray-100 px-3 py-1 text-xs text-gray-800">
                      {reasonLabel(t, reason)}
                    </span>
                  ))}
                </div>
              )}
              {rating.detail && <p className="text-sm text-gray-600">{detailLabel(t, rating.detail)}</p>}
            </div>
          )}
          {canEdit && (
            <div className="mt-3 flex items-center justify-between gap-3 border-t border-gray-100 pt-3">
              <p className="flex min-w-0 items-center gap-1.5 text-xs text-gray-500">
                <Lock className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
                <span>{t('Editable or deletable from this card.')}</span>
              </p>
              <div className="flex shrink-0 items-center gap-4">
                <button
                  type="button"
                  className="text-xs font-semibold text-gray-900 hover:underline disabled:opacity-50"
                  onClick={() => openWith(rating.score as number)}
                  disabled={isDeleting}
                >
                  {t('Edit')}
                </button>
                <button
                  type="button"
                  className="text-xs font-semibold text-gray-900 hover:underline disabled:opacity-50"
                  onClick={() => remove()}
                  disabled={isDeleting}
                >
                  {t('Delete')}
                </button>
              </div>
            </div>
          )}
        </>
      ) : (
      <div className="flex items-center gap-3">
        <div
          className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-gray-100 font-semibold text-gray-800"
          aria-hidden="true"
        >
          {initials(rating.technician.name)}
        </div>
        <div className="min-w-0">
          <h3 className="text-base font-semibold text-gray-900">{t('Rate the service')}</h3>
          <p className="truncate text-sm text-gray-500">
            {t('Intervention carried out by {{name}}', { name: rating.technician.name })}
          </p>
        </div>
      </div>
      )}

      {rating.score == null && canEdit && (
        <>
          <div className="mt-5">
            <StarRating value={0} onChange={openWith} size="lg" className="w-full justify-between px-1 sm:px-6" />
          </div>
          <p className="mt-5 flex items-center gap-1.5 text-xs text-gray-500">
            <Lock className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
            {t('Optional. Rating anonymised before transmission.')}
          </p>
        </>
      )}

      {rating.score == null && !canEdit && (
        <p className="mt-4 text-sm italic text-gray-500">{t('Not rated yet')}</p>
      )}



      {canEdit && (
        <Drawer open={open} onOpenChange={setOpen}>
          <DrawerContent aria-describedby={undefined}>
            <DrawerTitle className="sr-only">{t('Rate the service')}</DrawerTitle>
            <RatingForm
              key={`${preselected}-${open}`}
              technicianName={rating.technician.name}
              issueKey={rating.issueKey}
              initialScore={preselected}
              initialReasons={rating.score != null ? rating.reasons : []}
              initialDetail={rating.score != null ? rating.detail : null}
              onSubmit={submit}
              onLater={() => setOpen(false)}
              isPending={isRating}
            />
          </DrawerContent>
        </Drawer>
      )}
    </div>
  );
};

export default RatingCard;
